import styled, { css } from 'styled-components';
import { mobile_xl } from '../../global/mediaQueryMixin';
import { Typography } from '../../global/theme';
import { AlignCenter, FlexCenter } from '../../global/commonStyle';

interface Props {
  isDragging?: boolean;
}

interface PreviewImageProps {
  zoom: number;
  rotation: number;
  flipH: boolean;
  flipV: boolean;
  position: { x: number; y: number };
  isDragging: boolean;
}

export const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  overflow: hidden;
  &.image-preview {
    max-width: 300px;
    max-height: 300px;
    width: auto;
    height: auto;
    border-radius: var(--border-radius-6);
    border: 0.5px solid var(--border-primary);
  }

  &.print-screen-image img {
    max-width: 300px;
    max-height: 300px;
    width: 100%;
    height: 100%;
    object-fit: fill;
  }

  &.figma-image {
    display: flex;
    margin-inline: auto;
  }
  :hover.image-preview {
    border: 0.5px solid var(--text-primary);
    transition: border var(--transition-normal) ease;
  }
`;

export const StyledImage = styled.img<Props>`
  max-width: 300px;
  max-height: 300px;
  width: auto;
  height: auto;
  cursor: grab;
  object-fit: contain;
  ${(props) =>
    props.isDragging &&
    css`
      cursor: grabbing;
    `}
  ${mobile_xl(css`
    width: auto;
    max-width: 100%;
  `)}
`;

export const PreviewWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  ${FlexCenter}
  flex-direction: column;
  z-index: var(--z-index-modal);
  background-color: var(--preview-image-overlay);
  cursor: default;
  touch-action: none;
`;

export const PreviewHeader = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  ${AlignCenter}
  gap: var(--space-24);
  border-radius: var(--border-radius-8);
  z-index: var(--z-index-toast);
`;

export const PreviewActions = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
`;

export const ActionButton = styled.button`
  ${FlexCenter}
  padding: var(--space-11);
  background: var(--background-overlay-2);
  border: none;
  border-radius: var(--border-radius-full);
  cursor: pointer;
  transition: background-color var(--transition-fast);

  &:hover {
    background: var(--background-thumb-overlay);
  }
`;

export const CloseButton = styled(ActionButton)`
  margin-left: auto;
`;

export const Divider = styled.div`
  width: 1px;
  height: 32px;
  background-color: var(--border-secondary);
`;

export const PreviewImage = styled.img<PreviewImageProps>`
  max-width: 100%;
  max-height: 70%;
  width: auto;
  height: auto;
  object-fit: contain;
  user-select: none;
  touch-action: none;
  /* Note: Transform calculations here are acceptable as they're based on dynamic user interactions
     (zoom, rotation, drag position). CSS transform is the correct approach for these interactive values. */
  transform: scale(${(props) => props.zoom}) rotate(${(props) => props.rotation}deg)
    scaleX(${(props) => (props.flipH ? -1 : 1)}) scaleY(${(props) => (props.flipV ? -1 : 1)})
    translate(${(props) => props.position.x}px, ${(props) => props.position.y}px);
  transition: ${(props) => (props.isDragging ? 'none' : 'transform var(--transition-normal) ease-out')};
`;

export const ScreenshotToolbar = styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  ${AlignCenter}
  padding: var(--space-6);
  background: var(--background-overlay-2);
  border-radius: var(--border-radius-full);
  backdrop-filter: blur(8px);
  z-index: var(--z-index-toast);
  ${mobile_xl(css`
    bottom: 82px;
  `)}
`;

export const ToolbarGroup = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
`;

export const ToolbarButton = styled.button`
  ${FlexCenter}
  padding: var(--space-6);
  background: transparent;
  border: none;
  border-radius: var(--border-radius-4);
  color: var(--text-primary);
  transition: background-color var(--transition-fast);
  border-radius: var(--border-radius-full);
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  &:hover {
    background: var(--background-thumb-overlay);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ImageDiv = styled.div`
  position: absolute;
  inset: 0;
  ${FlexCenter}
  touch-action: none;
  img {
    max-height: 70%;
    vertical-align: middle;
    cursor: grab;
    user-select: none;
  }
`;

export const ImageMask = styled.div<{ showImageBorder?: boolean }>`
  position: absolute;
  inset: 0;
  ${FlexCenter}
  cursor: zoom-in;
  opacity: 0;
  transition: opacity var(--transition-normal);
  z-index: var(--z-index-base);
  // Show mask on hover of the container
  &:hover {
    ${(props) =>
      props.showImageBorder &&
      `
        border: 0.5px solid var(--text-primary);
        border-radius: var(--border-radius-4);
      `}
  }
  ${ImageContainer}:hover & {
    opacity: 1;
  }
`;

export const ImageMaskContent = styled.div`
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: var(--z-index-base);
  ${AlignCenter}
  padding: var(--space-4);
  gap: var(--space-4);
  border-radius: var(--border-radius-8);
  border: 0.5px solid var(--border-primary);
  background: var(--background-primary);
  max-width: 88px;
  opacity: 1;
  cursor: pointer;
`;

export const Overlay = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  pointer-events: none;
  z-index: var(--z-index-base);
`;

export const ProgressPill = styled.div`
  padding: var(--space-9) var(--space-8) var(--space-7);
  border-radius: var(--border-radius-8);
  background: var(--background-primary);
  color: var(--text-primary);
  ${Typography.body_sm}
  border: 0.5px solid var(--border-primary)
`;
